<template>
  <BlokkliProvider
    v-slot="{ entity, isEditing }"
    v-bind="blokkliProps"
    :entity="props"
  >
    <BlokkliField
      v-slot="{ items }"
      :list="paragraphs"
      name="field_paragraphs"
      class="page-paragraphs"
    >
      <PageTitleWithImage
        :tagline="exhibitionType?.name"
        :image="entity.image || image"
        :blokkli-fields="{
          image: 'field_image',
        }"
      >
        <template #title>
          <span v-blokkli-editable:title>{{ entity?.title || title }}</span>
        </template>

        <template v-if="dates && !hideDate" #date>
          <FieldItemTypeDateRange v-bind="dates" />
        </template>

        <template #after>
          <InPageNavigation :paragraphs="items" class="mt-6" />
        </template>
      </PageTitleWithImage>
    </BlokkliField>

    <Locations
      v-if="locations && !exhibitionIsInThePast && !hideStickybar"
      :locations="locations"
      :sticky="!isEditing"
      :show-link="exhibitionIsInTheFuture"
      :show-status="exhibitionIsCurrent"
      :hide-location="hideLocation"
    />
  </BlokkliProvider>
</template>

<script lang="ts" setup>
import type { NodeExhibitionFragment } from '#graphql-operations'
import { ExhibitionTimelineStatus } from '#graphql-operations'

const props = defineProps<{
  uuid?: string
  id?: string
  title?: string
  image?: NodeExhibitionFragment['image']
  paragraphs?: NodeExhibitionFragment['paragraphs']
  dates?: NodeExhibitionFragment['dates']
  locations?: NodeExhibitionFragment['locations']
  timelineStatus?: NodeExhibitionFragment['timelineStatus']
  locationAddresses?: NodeExhibitionFragment['locationAddresses']
  exhibitionType?: NodeExhibitionFragment['exhibitionType']
  targetGroups?: NodeExhibitionFragment['targetGroups']
  blokkliProps: NodeExhibitionFragment['blokkliProps']
  hideDate?: boolean
  hideLocation?: boolean
  hideStickybar?: boolean
}>()

const datePrint = computed(() => props.dates?.datePrint || '')

defineVisitorTableData((data) => {
  if (!props.hideDate) {
    data.add(
      'date',
      {
        ...props.dates,
        datePrint: datePrint?.value,
      },
      datePrint,
    )
  }
  if (!props.hideLocation) {
    data.add(
      'address',
      props.locationAddresses?.map((v) => {
        return {
          ...v.address,
          ...v.addressLink,
        }
      }),
    )
  }
  data.add('target_group', props.targetGroups)
})

const exhibitionId = computed(() => props.id)

const exhibitionIsCurrent = computed(
  () => props.timelineStatus === ExhibitionTimelineStatus.CURRENT,
)

const exhibitionIsInThePast = computed(
  () => props.timelineStatus === ExhibitionTimelineStatus.PAST,
)

const exhibitionIsInTheFuture = computed(
  () => props.timelineStatus === ExhibitionTimelineStatus.FUTURE,
)

provide('exhibition_id', exhibitionId)

defineOptions({
  name: 'NodeExhibition',
})
</script>
